import { Dispatch } from 'redux'

import {
  getAll,
  create,
  update,
  destroy,
  addCompanyToCompanyGroup,
  getOntologySyncStatus,
  removeCompanyFromCompanyGroup
} from 'redux/api/CompanyGroups'
import { startJobRun } from 'redux/api/Applications/DataProducts/v3'
import { toggleSnackbarOpen } from '../Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'

import { useThunkActions } from 'redux/actions/useThunkActions'

import * as actions from './actions'

export function tryGetAllCompanyGroups(dispatch: Dispatch): Promise<void> {
  dispatch(actions.getAll())

  // Never rejects — failures dispatch getAllFailed so that app
  // initialization (Promise.all in initializeApp) always proceeds.
  return getAll()
    .then((response) => {
      dispatch(actions.getAllSuccess(response.data))
    })
    .catch(() => {
      dispatch(actions.getAllFailed())
    })
}

export function tryCreateCompanyGroup(
  name: string,
  company?: string,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(name, company)
      .then((response) => {
        dispatch(actions.createSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupCreated))

        if (callback) {
          callback()
        }
      })
      .catch(() => {
        dispatch(actions.createFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupCreatedFailed))

        if (callback) {
          callback(true)
        }
      })
  }
}

export function tryUpdateCompanyGroup(
  id: string,
  name?: string,
  economySetId?: string | null,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, name, economySetId)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupUpdated))

        if (callback) {
          callback()
        }
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupUpdatedFailed))

        if (callback) {
          callback(true)
        }
      })
  }
}

export function tryDeleteCompanyGroup(
  id: string,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess({ id }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupDeleted))

        if (callback) {
          callback()
        }
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupDeletedFailed))

        if (callback) {
          callback(true)
        }
      })
  }
}

export function tryAddCompanyToCompanyGroup(
  id: string,
  companyId: string,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.addCompany())

    addCompanyToCompanyGroup(id, companyId)
      .then((response) => {
        dispatch(actions.addCompanySuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupUpdated))

        if (callback) {
          callback()
        }
      })
      .catch(() => {
        dispatch(actions.addCompanyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupUpdatedFailed))

        if (callback) {
          callback(true)
        }
      })
  }
}

export function tryRemoveCompanyFromCompanyGroup(
  id: string,
  companyId: string,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.removeCompany())

    removeCompanyFromCompanyGroup(id, companyId)
      .then((response) => {
        dispatch(actions.removeCompanySuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupUpdated))

        if (callback) {
          callback()
        }
      })
      .catch(() => {
        dispatch(actions.removeCompanyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.CompanyGroupUpdatedFailed))

        if (callback) {
          callback(true)
        }
      })
  }
}

export function tryGetCompanyStructureSyncStatus() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getSyncStatus())

    getOntologySyncStatus()
      .then((response) => {
        dispatch(actions.getSyncStatusSuccess(response.data))
      })
      .catch(() => {
        dispatch(actions.getSyncStatusFailed())
      })
  }
}

export function tryRerunOntology(
  dataProductId: string,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.rerunOntology())

    startJobRun(dataProductId)
      .then(() => {
        dispatch(actions.rerunOntologySuccess())

        dispatch(toggleSnackbarOpen(SnackbarMessage.OntologyRerunStarted))

        if (callback) {
          callback()
        }
      })
      .catch(() => {
        dispatch(actions.rerunOntologyFailed())

        dispatch(
          toggleSnackbarOpen(SnackbarMessage.OntologyRerunStartedFailed)
        )

        if (callback) {
          callback(true)
        }
      })
  }
}

export const useCompanyGroupActions = () => {
  return {
    tryAddCompanyToCompanyGroup: useThunkActions(tryAddCompanyToCompanyGroup),
    tryCreateCompanyGroup: useThunkActions(tryCreateCompanyGroup),
    tryDeleteCompanyGroup: useThunkActions(tryDeleteCompanyGroup),
    tryGetCompanyStructureSyncStatus: useThunkActions(
      tryGetCompanyStructureSyncStatus
    ),
    tryRemoveCompanyFromCompanyGroup: useThunkActions(
      tryRemoveCompanyFromCompanyGroup
    ),
    tryRerunOntology: useThunkActions(tryRerunOntology),
    tryUpdateCompanyGroup: useThunkActions(tryUpdateCompanyGroup)
  }
}
